import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAlignmentComparison } from '../../../../hooks/useAlignmentComparison';

interface AlignmentComparisonTableProps {
  projectId?: string;
  dataType: 'consumption' | 'price';
  isAligned: boolean;
  title: string;
}

export const AlignmentComparisonTable: React.FC<AlignmentComparisonTableProps> = ({
  projectId,
  dataType,
  isAligned,
  title
}) => {
  const { t } = useTranslation();
  const { data: comparisonData, isLoading } = useAlignmentComparison(
    projectId,
    dataType,
    isAligned
  );

  if (!isAligned) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="text-xs text-swiss-stone">
        {t('dataAlignment.comparison.loadingData')}
      </div>
    );
  }

  if (!comparisonData || !comparisonData.samples) {
    return null;
  }

  const { samples, summary, unit } = comparisonData;

  return (
    <div className="space-y-3">
      {/* Summary - Match upload validation style exactly */}
      <div className="p-3 bg-white rounded border border-swiss-mist">
        <div className="text-xs text-swiss-charcoal">
          <span className="font-medium">{t('dataAlignment.comparison.records')}:</span> {summary.original_records} {t('dataAlignment.comparison.original')} → {summary.aligned_records} {t('dataAlignment.comparison.aligned')}
          <span className="ml-2 text-swiss-stone">({summary.interpolated_records} {t('dataAlignment.comparison.interpolated')})</span>
          {summary.avg_diff_pct === 0 && (
            <span className="ml-2 text-brand-primary font-semibold">✓ {t('dataAlignment.comparison.perfectMatch')}</span>
          )}
          {summary.avg_diff_pct > 0 && summary.avg_diff_pct < 1 && (
            <span className="ml-2 text-brand-primary font-semibold">✓ {t('dataAlignment.comparison.excellent', { diff: summary.avg_diff_pct.toFixed(2) })}</span>
          )}
          {summary.avg_diff_pct >= 1 && (
            <span className="ml-2 text-orange-600 font-semibold">⚠ {summary.avg_diff_pct.toFixed(2)}% {t('dataAlignment.comparison.avgDiff')}</span>
          )}
          <span className="ml-2 text-swiss-stone">• {t('dataAlignment.comparison.method')}: <span className="capitalize">{summary.completion_method}</span></span>
        </div>
      </div>

      {/* Sample Rows Table - Clean minimal design (matches upload validation) */}
      <div className="overflow-x-auto rounded border border-swiss-mist">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-swiss-mist bg-white">
              <th className="px-3 py-2 text-left text-swiss-stone font-medium">{t('dataAlignment.comparison.sample')}</th>
              <th className="px-3 py-2 text-left text-swiss-stone font-medium">{t('dataAlignment.comparison.originalTime')}</th>
              <th className="px-3 py-2 text-right text-swiss-stone font-medium">{t('dataAlignment.comparison.original')} {unit}</th>
              <th className="px-3 py-2 text-left text-swiss-stone font-medium">{t('dataAlignment.comparison.alignedTime')}</th>
              <th className="px-3 py-2 text-right text-swiss-stone font-medium">{t('dataAlignment.comparison.aligned')} {unit}</th>
              <th className="px-3 py-2 text-center text-swiss-stone font-medium">{t('dataAlignment.comparison.diffPct')}</th>
              <th className="px-3 py-2 text-center text-swiss-stone font-medium">✓</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {samples.map((sample, idx) => (
              <tr
                key={idx}
                className={`border-b border-swiss-mist last:border-0 hover:bg-swiss-pearl/20 ${
                  sample.is_interpolated ? 'bg-blue-50' : ''
                }`}
              >
                <td className="px-3 py-2 text-swiss-stone text-[10px] font-medium">
                  {sample.label}
                  {sample.is_interpolated && (
                    <span className="ml-1 text-blue-600">*</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-swiss-charcoal">
                  {sample.original_timestamp === '—' ? (
                    <span className="text-swiss-stone">—</span>
                  ) : (
                    sample.original_timestamp
                  )}
                </td>
                <td className="px-3 py-2 text-right text-swiss-charcoal">
                  {typeof sample.original_value === 'number'
                    ? sample.original_value.toFixed(1)
                    : <span className="text-swiss-stone">—</span>
                  }
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-swiss-charcoal">
                  {sample.aligned_timestamp}
                </td>
                <td className="px-3 py-2 text-right text-swiss-charcoal">
                  {sample.aligned_value.toFixed(1)}
                </td>
                <td className="px-3 py-2 text-center text-swiss-stone text-[10px]">
                  {sample.diff_pct !== null && sample.diff_pct !== undefined ? (
                    <span className={sample.diff_pct > 1 ? 'text-orange-600 font-medium' : ''}>
                      {sample.diff_pct.toFixed(2)}%
                    </span>
                  ) : (
                    <span className="text-swiss-stone">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {sample.match === true ? (
                    <span className="text-brand-primary font-bold text-sm">✓</span>
                  ) : sample.match === false ? (
                    <span className="text-orange-600 font-bold text-sm">✗</span>
                  ) : (
                    <span className="text-swiss-stone text-sm">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend - Minimal style */}
      <div className="text-[10px] text-swiss-stone">
        <span className="text-blue-600 font-bold">*</span> {t('dataAlignment.comparison.interpolatedNote')} •
        <span className="ml-1">{t('dataAlignment.comparison.sampleDescription')}</span>
      </div>
    </div>
  );
};
